import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { FeedbackRights } from '../../feedback/providers/feedback-rights';
import { FeedbackQuestionFactoryService } from '../../feedback/questions/feedback-question-factory.service';
import { CurrentUser } from "../../common/providers/current-user";
import { ActivatedRoute, Router } from '@angular/router';
import { QuestionProvider } from '../../feedback/providers/question-provider';
import { UsersProvider } from '../../users/providers/users-provider';
import { UtilsProvider } from "../../common/providers/utils-provider.service";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CurrentUserRolesProvider } from '../../organizations/providers/current-user-roles-provider';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../config-provider.service";
import { FeedbackQuestionSurvey } from '../../feedback/providers/athlete-question-answer-provider';
import { SessionsProvider } from '../../sessions/providers/sessions-provider';
import { Subscription } from 'rxjs';
import { formatDate, NgIf } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Organization } from "../../common/models/organization";
import { Feedback } from "../../common/models/feedback";
import { Session } from "../../common/models/session";
import { Timeslot } from "../../common/models/timeslot";
import { User } from "../../common/models/user";
import { MobilePageContentComponent } from "../../common/components/mobile-page-content/mobile-page-content";
import { MobileHeaderComponent } from "../../common/components/mobile-header/mobile.header";
import { MobileSideMenuComponent } from "../../common/components/mobile-side-menu/mobile.side-menu.component";
import { Error401Component } from "../../common/components/error-401/error-401";
import { Error404Component } from "../../common/components/error-404/error-404";
import { ModuleContentComponent } from "../../common/components/module-content/module-content";

@Component({
  selector: 'app-mobile-feedback-session-new-container',
  templateUrl: './mobile.feedback-session-new-container.component.html',
  styleUrls: ['./mobile.feedback-session-new-container.scss'],
  imports: [
    MobilePageContentComponent,
    MobileHeaderComponent,
    MobileSideMenuComponent,
    Error401Component,
    Error404Component,
    ModuleContentComponent,
    NgIf,
    TranslateModule
  ],
  standalone: true
})
export class MobileFeedbackSessionNewContainerComponent implements OnInit, AfterViewInit, OnDestroy {

    public pageTitle = 'Tests';
    public isLoading = true;
    public is401 = false;
    public is404 = false;

    public organization: Organization = {} as Organization;
    public feedback: Feedback = {} as Feedback;
    public timeslot: Timeslot = {} as Timeslot
    public athleteUser: User = {} as User;

    public questions: FeedbackQuestionSurvey[] = [];

    public questionsLoaded: EventEmitter<any> = new EventEmitter();
    public rights: FeedbackRights = new FeedbackRights(this.utils);

    @ViewChild('questionComponentRef', {
        static: false,
        read: ViewContainerRef
    })
    public viewContainerRef: ViewContainerRef= {} as ViewContainerRef;

    public isSubmitting = false;

    private service;
    private subscriptions: Subscription[] = [];

    constructor(
    @Inject(FeedbackQuestionFactoryService) service: FeedbackQuestionFactoryService,
                                            private currentUser: CurrentUser,
                                            private route: ActivatedRoute,
                                            private questionProvider: QuestionProvider,
                                            private router: Router,
                                            private toaster: MatSnackBar,
                                            private translator: TranslateService,
                                            private sessionProvider: SessionsProvider,
                                            private userProvider: UsersProvider,
                                            private utils: UtilsProvider,
                                            private currentUserRoles: CurrentUserRolesProvider,
                                            private title: Title,
                                            public conf: ConfigProvider,
                                            protected feedbackQuestionFactoryService: FeedbackQuestionFactoryService
    ) {
        this.service = service;

        this.subscriptions.push(
            feedbackQuestionFactoryService.submitSubject.subscribe((res) => {
                this.isSubmitting = res;
            })
        );
    }

    public ngOnInit(): void {
        // Load athlete and annual plan if it exists
        this.subscriptions.push(
            this.route.data.subscribe((data: any) => {//{ organization: Organization; athlete: Athlete; feedback: Feedback; timeslot: Timeslot }
                if (data.organization) {
                    this.organization = data.organization;
                    this.service.setOrganization(data.organization);
                }

                if (data.athlete) {
                  this.athleteUser = data.athlete;
                  this.service.setAthlete(data.athlete.athlete);
                }

                if (data.feedback) {
                    this.feedback = data.feedback;
                    this.service.setFeedback(data.feedback);
                }

                if (data.timeslot) {
                    this.timeslot = data.timeslot;
                    this.service.setTimeslot(data.timeslot);
                }

                Promise.all([this.loadQuestionData()]).then(() => {

                    this.createPageTitle();

                    this.rights.evaluateScreenRights({
                        rolesIds: this.currentUserRoles.getRolesIds(this.organization.id!)
                    });

                    if (!this.rights.ADD) {
                        this.is401 = true;
                    }

                    this.isLoading = false;
                    this.questionsLoaded.emit();
                });

                this.subscriptions.push(
                    this.service.surveySaved.subscribe((event) => {
                        const date = formatDate(this.utils.firstDayOfWeek(event.sessionDate), 'yyyy-M-d', 'en');
                        this.router.navigate([`/${event.organizationCode}/app/athletes/${event.userId}/diary/${date}`]);
                    })
                );
            })
        );
    }

    public ngAfterViewInit(): void {

        this.service.setRootViewContainerRef(this.viewContainerRef);

        this.subscriptions.push(
            this.questionsLoaded.subscribe(() => {
                this.service.startSurvey();
            })
        );
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    public loadQuestionData(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.subscriptions.push(
                this.questionProvider.findAll(this.organization, this.feedback).subscribe((questions: FeedbackQuestionSurvey[]) => {
                    this.questions = questions;
                    this.service.questions = questions;
                    resolve(null);
                })
            );
        });
    }

    private createPageTitle() {
        this.pageTitle = this.translator.instant('FeedbackSessionNew');
        this.title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }
}
